import * as React from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";
import PaymentsHeader from "./PaymentsHeader";

import {
  Grid,
  Card,
  CardHeader,
  CardContent,
  Button,
  Modal,
  Box,
  Divider,
  Typography,
  Backdrop,
  CircularProgress,
} from "@mui/material";

import { DataGrid, GridCellParams, GridColDef } from "@mui/x-data-grid";

import axios from "../../helpers/axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { EditOutlined } from "@mui/icons-material";

export interface IProps {
  user: {
    id: number;
    name: string;
    email: string;
    role: string;
    is_active: boolean;
    selectedHierarchyIds: any;
  };
}

export interface IState {
  rows: any[];
  modalOpen: boolean;
  selectedRow: any | null;
  loading: boolean;
}

const modalStyle = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 380,
  bgcolor: "background.paper",
  borderRadius: 2,
  p: 3,
  boxShadow: 24,
};

class BagWeightsForReview extends React.Component<
  RouteComponentProps & IProps,
  IState
> {
  constructor(props: RouteComponentProps & IProps) {
    super(props);

    this.state = {
      rows: [],
      modalOpen: false,
      selectedRow: null,
      loading: true,
    };
  }

  componentDidMount(): void {
    this.fetchLogs();
  }

  /** ⭐ Fetch logs with hierarchy IDs */
  fetchLogs = async () => {
    this.setState({ loading: true });

    try {
      const res = await axios.get("/weighingScaleLogs", {
        params: { hierarchies: this.props.user.selectedHierarchyIds },
      });

      if (!res.data.success && res.data.msg) {
        toast.error(res.data.msg);
      }

      const rows = res.data.data.map((item: any) => ({
        id: item.id,
        city: item.city_name,
        agent: item.agent_name,
        weight: item.weight,
        date: item.date,
        barcode: item.barcode,
        images: item.images || [],
        review_status: item.review_status,
        reviewed_by_name: item.reviewed_by_name,
      }));

      this.setState({ rows, loading: false });
    } catch (err: any) {
      console.error(err);
      toast.error("Error loading data!");
      this.setState({ loading: false });
    }
  };

  /** Open review modal */
  openReviewModal = (row: any) => {
    this.setState({ modalOpen: true, selectedRow: row });
  };

  /** ⭐ Review API */
  confirmReview = async () => {
    if (!this.state.selectedRow) return;

    this.setState({ loading: true });

    try {
      const res = await axios.post("/weighingScaleLogs/review", {
        id: this.state.selectedRow.id,
        reviewed_by_id: this.props.user.id,
        reviewed_by_name: this.props.user.name,
      });

      if (res.data.success) {
        toast.success("Marked as Reviewed Successfully!");
      } else {
        toast.error(res.data.msg ?? "Failed to update!");
      }

      this.setState({ modalOpen: false, selectedRow: null });
      this.fetchLogs();
    } catch (err: any) {
      console.error(err);

      if (err.response?.data?.errors) {
        Object.values(err.response.data.errors).forEach((e: any) =>
          toast.error(e[0])
        );
      } else {
        toast.error("Something went wrong!");
      }

      this.setState({ loading: false });
    }
  };

  render() {
    const columns: GridColDef[] = [
      { field: "id", headerName: "ID", width: 80 },
      { field: "city", headerName: "City", width: 150 },
      { field: "agent", headerName: "Agent Name", width: 180 },
      { field: "date", headerName: "Date", width: 150 },
      { field: "barcode", headerName: "Barcode", width: 160 },
      { field: "weight", headerName: "Weight (Kg)", width: 120 },
      {
        field: "images",
        headerName: "Images",
        width: 250,
        renderCell: (params: GridCellParams) => (
          <div style={{ display: "flex", flexDirection: "column" }}>
            {params.row.images.map((img: string, idx: number) => {
              const path = img.split("amazonaws.com/")[1]; // B102.../1764...
              const folderName = path?.split("/")[0]; // B102918517
              const fileName = `${folderName}.jpg`; // B102918517.jpg

              return (
                <a
                  key={idx}
                  href={img}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "#0B7A32",
                    fontWeight: 600,
                    textDecoration: "none", // no underline
                    cursor: "pointer",
                    marginBottom: 4,
                  }}
                >
                  {fileName}
                </a>
              );
            })}
          </div>
        ),
      },
      {
        field: "review_status",
        headerName: "Review Status",
        width: 250,
        renderCell: (params: GridCellParams) =>
          params.row.review_status === "Pending" ? (
            <Button
              variant="contained"
              size="small"
              onClick={() => this.openReviewModal(params.row)}
              style={{ backgroundColor: "#0B7A32" }}
            >
              Mark as Reviewed
            </Button>
          ) : (
            <strong style={{ color: "green" }}>
              Reviewed by {params.row.reviewed_by_name}
            </strong>
          ),
      },
    ];

    return (
      <div>
        {/* ⭐ SINGLE LOADER (ONLY BACKDROP) */}
        <Backdrop
          sx={{ zIndex: (theme) => theme.zIndex.drawer + 1000 }}
          open={this.state.loading}
        >
          <CircularProgress className="customLoader" />
        </Backdrop>

        {/* ⭐ Toast Container */}
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar
          newestOnTop
          closeOnClick
          pauseOnHover
          draggable
        />

        <PaymentsHeader user={this.props.user} />

        <Grid container spacing={2}>
          <Grid item md={12} xs={12} className="mt-3">
            <Card>
              <CardHeader
                title="Bag Weights For Review"
                className="text-left"
              />
              <CardContent>
                <DataGrid
                  rows={this.state.rows}
                  columns={columns}
                  autoHeight
                  disableSelectionOnClick
                  components={{
                    MoreActionsIcon: EditOutlined,
                  }}
                  // ❌ Removed loading={this.state.loading}
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: "6px",
                    fontFamily: "Inter, sans-serif",
                    "& .MuiDataGrid-columnHeaders": {
                      backgroundColor: "#ffffff",
                      fontWeight: 600,
                      fontSize: "15px",
                    },
                    "& .MuiDataGrid-cell": {
                      fontSize: "14px",
                    },
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* ⭐ Confirm Modal */}
        <Modal
          open={this.state.modalOpen}
          onClose={() => this.setState({ modalOpen: false })}
        >
          <Box sx={modalStyle}>
            <Typography variant="h6" mb={2}>
              Confirm Review
            </Typography>

            <Divider />

            <Typography mt={2} mb={3}>
              Are you sure you want to mark this entry as reviewed?
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() =>
                    this.setState({
                      modalOpen: false,
                      selectedRow: null,
                    })
                  }
                >
                  Cancel
                </Button>
              </Grid>

              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={this.confirmReview}
                  style={{ backgroundColor: "#0B7A32" }}
                >
                  Yes
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Modal>
      </div>
    );
  }
}

export default withRouter(BagWeightsForReview);
